/**
 * Tahap 8A — HR Core Frontend: Hub + Employee List + Employee Edit + Policy list.
 * Bahasa Indonesia.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah } from "@/lib/format";
import { Users, ShieldCheck, FileText, Building2, Plus, Eye, ScrollText } from "lucide-react";

const PERMS_HR = {
  super_admin: ["*"],
  hrd_manager: ["HR_EMPLOYEE_LIHAT", "HR_EMPLOYEE_KELOLA", "HR_POLICY_LIHAT", "HR_POLICY_KELOLA"],
  hr_staff: ["HR_EMPLOYEE_LIHAT", "HR_POLICY_LIHAT"],
  payroll_maker: ["HR_EMPLOYEE_LIHAT", "HR_SALARY_LIHAT", "HR_POLICY_LIHAT"],
  payroll_approver: ["HR_EMPLOYEE_LIHAT", "HR_SALARY_LIHAT"],
  direktur_utama: ["HR_EMPLOYEE_LIHAT", "HR_POLICY_KELOLA"],
  direktur_operasional: ["HR_EMPLOYEE_LIHAT"],
  auditor: ["HR_EMPLOYEE_LIHAT", "HR_POLICY_LIHAT"],
  viewer: [],
  employee: [],
};

function useCanHR() {
  const { user } = useAuth();
  const role = user?.role;
  const perms = PERMS_HR[role] || [];
  const isSuper = role === "super_admin" || perms.includes("*");
  return (p) => isSuper || perms.includes(p);
}

function NoAccessBanner({ perm }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900" data-testid="no-access-hr">
      <ShieldCheck className="inline mr-2" size={18} />
      Anda belum memiliki izin <b>{perm}</b> untuk membuka halaman HR ini.
    </div>
  );
}

export function HRHub() {
  const can = useCanHR();
  const items = [
    { to: "/hr/karyawan", icon: Users, title: "Master Karyawan",
      desc: "Data karyawan, kontrak, dokumen, riwayat", perm: "HR_EMPLOYEE_LIHAT" },
    { to: "/hr/absensi-hub", icon: Users, title: "Absensi & Waktu Kerja",
      desc: "Kalender Indonesia, absensi, cuti, lembur, koreksi (8B)", perm: "HR_EMPLOYEE_LIHAT" },
    { to: "/hr/policy", icon: ScrollText, title: "HR Policy Engine",
      desc: "Working hours, BPJS, cuti, THR, overtime — versioned + effective-dated", perm: "HR_POLICY_LIHAT" },
    { to: "/hr/organisasi", icon: Building2, title: "Struktur Organisasi",
      desc: "Jabatan & departemen", perm: "HR_EMPLOYEE_LIHAT" },
  ];
  return (
    <div className="space-y-4" data-testid="hr-hub">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">HR &amp; Admin</h1>
        <p className="text-sm text-slate-600">Tahap 8A — HR Core &amp; Employee Database. Berikutnya: Attendance, Payroll, Claim, Recruitment, Onboarding.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const allowed = can(it.perm);
          return (
            <Card key={it.to} className={allowed ? "hover:shadow-md transition" : "opacity-50"}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-indigo-50 p-3"><it.icon className="text-indigo-700" size={22} /></div>
                  <div className="flex-1">
                    <div className="text-base font-semibold text-slate-900">{it.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{it.desc}</div>
                    {allowed ? (
                      <Link to={it.to} className="mt-3 inline-block text-sm text-indigo-700 hover:underline"
                        data-testid={`hr-hub-link-${it.title.replaceAll(" ", "-")}`}>Buka →</Link>
                    ) : (
                      <div className="mt-3 text-xs text-amber-700">Perlu izin: {it.perm}</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export function EmployeeListPage() {
  const can = useCanHR();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const posMap = React.useMemo(
    () => Object.fromEntries(positions.map((p) => [p.code, p.name])), [positions]);
  const depMap = React.useMemo(
    () => Object.fromEntries(departments.map((d) => [d.code, d.name])), [departments]);
  const load = async () => {
    if (!can("HR_EMPLOYEE_LIHAT")) return;
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (search) q.set("search", search);
      if (status) q.set("status", status);
      const [emp, pos, dep] = await Promise.all([
        api.get(`/hr/employees?${q.toString()}`),
        api.get(`/hr/org-positions`),
        api.get(`/hr/departments`),
      ]);
      setRows(emp.data);
      setPositions(pos.data);
      setDepartments(dep.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  if (!can("HR_EMPLOYEE_LIHAT")) return <NoAccessBanner perm="HR_EMPLOYEE_LIHAT" />;
  return (
    <div className="space-y-4" data-testid="hr-employee-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Master Karyawan</h1>
          <p className="text-sm text-slate-600">Data karyawan aktif. NIK/NPWP/rekening dienkripsi & di-mask sesuai izin.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Cari nama/kode…" value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className="w-64" data-testid="emp-search" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="emp-status-filter"><SelectValue placeholder="Semua status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Aktif</SelectItem>
              <SelectItem value="PROBATION">Probation</SelectItem>
              <SelectItem value="INACTIVE">Nonaktif</SelectItem>
              <SelectItem value="RESIGNED">Resign</SelectItem>
              <SelectItem value="TERMINATED">Diterminasi</SelectItem>
            </SelectContent>
          </Select>
          {can("HR_EMPLOYEE_KELOLA") && (
            <Button onClick={() => nav("/hr/karyawan/baru")} data-testid="btn-new-emp">
              <Plus size={16} className="mr-1" />Tambah Karyawan
            </Button>
          )}
        </div>
      </div>
      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Kode</TableHead><TableHead>Nama</TableHead>
            <TableHead>Jabatan</TableHead>
            <TableHead>Departemen</TableHead>
            <TableHead>Kontrak</TableHead>
            <TableHead>NIK KTP</TableHead>
            <TableHead className="text-right">Gaji Pokok</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={9}>Memuat…</TableCell></TableRow>
            : rows.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center text-slate-500">Belum ada karyawan.</TableCell></TableRow>
            : rows.map((r) => (
              <TableRow key={r.id} data-testid={`emp-row-${r.id}`}>
                <TableCell className="font-mono">{r.code}</TableCell>
                <TableCell>{r.full_name}</TableCell>
                <TableCell data-testid={`emp-position-${r.id}`}>{posMap[r.position_code] || r.position_code || "-"}</TableCell>
                <TableCell data-testid={`emp-department-${r.id}`}>{depMap[r.department_code] || r.department_code || "-"}</TableCell>
                <TableCell><Badge variant="outline">{r.contract_type}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{r.nik_ktp || "-"}</TableCell>
                <TableCell className="text-right">{r.current_basic_salary != null ? formatRupiah(r.current_basic_salary) : "—"}</TableCell>
                <TableCell><Badge>{r.status}</Badge></TableCell>
                <TableCell>
                  <Link to={`/hr/karyawan/${r.id}`} className="text-indigo-700 hover:underline text-sm"
                        data-testid={`emp-view-${r.id}`}>
                    <Eye size={14} className="inline mr-1" />Detail
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export function EmployeeEditPage() {
  const can = useCanHR();
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [emp, setEmp] = useState({
    full_name: "", contract_type: "PKWTT", join_date: "",
    work_email: "", personal_email: "", phone: "",
    nik_ktp: "", npwp: "", bank_code: "", bank_account_number: "",
    bank_account_holder: "", bpjs_kesehatan_number: "", bpjs_ketenagakerjaan_number: "",
    current_basic_salary: 0, status: "ACTIVE",
    marital_status: "", number_of_children: 0, education_level: "", ktp_address: "",
    auto_provision_user: true,
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateEmail = (v) => {
    if (!v || String(v).trim() === "") { setEmailError(""); return true; }
    if (!EMAIL_RE.test(String(v).trim())) { setEmailError("Format email tidak valid."); return false; }
    setEmailError(""); return true;
  };
  // Load position/department masters once
  useEffect(() => {
    api.get(`/hr/org-positions?active_only=true`).then((r) => setPositions(r.data)).catch(() => {});
    api.get(`/hr/departments?active_only=true`).then((r) => setDepartments(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!id) return;
    api.get(`/hr/employees/${id}`).then((r) => setEmp((prev) => ({ ...prev, ...r.data }))).catch((e) => toast.error(formatApiError(e)));
    api.get(`/hr/employees/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
  }, [id]);
  // When position changes, auto-fill department based on master mapping (unless Staff)
  const handlePositionChange = (newPosCode) => {
    const pos = positions.find((p) => p.code === newPosCode);
    const nextDept = pos && pos.department_code ? pos.department_code : emp.department_code;
    setEmp({ ...emp, position_code: newPosCode, department_code: nextDept || "" });
  };
  // Sanitize optional fields: convert "", "-", whitespace-only to omitted (null on backend)
  const sanitizePayload = (obj) => {
    const OPTIONAL_STR = ["work_email", "personal_email", "npwp", "nik_ktp",
      "phone", "bank_code", "bank_account_number", "bank_account_holder",
      "bpjs_kesehatan_number", "bpjs_ketenagakerjaan_number",
      "contract_end_date", "gender",
      "marital_status", "education_level", "ktp_address"];
    const out = { ...obj };
    OPTIONAL_STR.forEach((k) => {
      const v = out[k];
      if (v === undefined || v === null) { delete out[k]; return; }
      const s = String(v).trim();
      if (s === "" || s === "-") delete out[k];
      else out[k] = s;
    });
    return out;
  };
  // Handle marital status change — reset number_of_children to 0 if TIDAK_KAWIN
  const handleMaritalChange = (v) => {
    if (v === "TIDAK_KAWIN" && Number(emp.number_of_children) > 0 && id) {
      if (!window.confirm("Mengubah ke Tidak Kawin akan mengatur Jumlah Anak ke 0. Lanjutkan?")) return;
    }
    setEmp({ ...emp, marital_status: v, number_of_children: v === "TIDAK_KAWIN" ? 0 : emp.number_of_children });
  };
  const save = async () => {
    if (!validateEmail(emp.work_email)) {
      toast.error("Format email kantor tidak valid.");
      return;
    }
    if (emp.personal_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(emp.personal_email).trim())) {
      toast.error("Format email pribadi tidak valid.");
      return;
    }
    if (!emp.position_code) {
      toast.error("Jabatan wajib dipilih.");
      return;
    }
    if (!emp.department_code) {
      toast.error("Departemen wajib dipilih.");
      return;
    }
    setLoading(true);
    try {
      const payload = sanitizePayload(emp);
      if (isNew) {
        const r = await api.post("/hr/employees", payload);
        toast.success(`Karyawan ${r.data.code} berhasil dibuat.`);
        nav(`/hr/karyawan/${r.data.id}`);
      } else {
        await api.put(`/hr/employees/${id}`, { ...payload, change_reason: emp.change_reason || "Update" });
        toast.success("Karyawan diperbarui.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  if (!can("HR_EMPLOYEE_LIHAT")) return <NoAccessBanner perm="HR_EMPLOYEE_LIHAT" />;
  const canEdit = can("HR_EMPLOYEE_KELOLA");
  return (
    <div className="space-y-4" data-testid="hr-employee-edit">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{isNew ? "Karyawan Baru" : `Karyawan ${emp.code}`}</h1>
          <p className="text-sm text-slate-600">{emp.full_name || ""}</p>
        </div>
        {canEdit && <Button onClick={save} disabled={loading} data-testid="btn-save-emp">{loading ? "Menyimpan…" : "Simpan"}</Button>}
      </div>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Data Pribadi</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><Label>Nama Lengkap*</Label><Input value={emp.full_name || ""} onChange={(e) => setEmp({ ...emp, full_name: e.target.value })} disabled={!canEdit} data-testid="emp-full-name" /></div>
          <div><Label>NIK KTP</Label><Input value={emp.nik_ktp || ""} onChange={(e) => setEmp({ ...emp, nik_ktp: e.target.value })} disabled={!canEdit} data-testid="emp-nik" /></div>
          <div><Label>NPWP</Label><Input value={emp.npwp || ""} onChange={(e) => setEmp({ ...emp, npwp: e.target.value })} disabled={!canEdit} data-testid="emp-npwp" /></div>
          <div><Label>Jenis Kelamin</Label>
            <Select value={emp.gender || ""} onValueChange={(v) => setEmp({ ...emp, gender: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-gender"><SelectValue placeholder="Pilih" /></SelectTrigger>
              <SelectContent><SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Status Perkawinan</Label>
            <Select value={["TIDAK_KAWIN","KAWIN","DUDA","JANDA"].includes(emp.marital_status) ? emp.marital_status : (emp.marital_status === "BELUM_KAWIN" ? "TIDAK_KAWIN" : "")} onValueChange={handleMaritalChange} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-marital"><SelectValue placeholder="Pilih status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TIDAK_KAWIN">Tidak Kawin</SelectItem>
                <SelectItem value="KAWIN">Kawin</SelectItem>
                <SelectItem value="DUDA">Duda</SelectItem>
                <SelectItem value="JANDA">Janda</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Jumlah Anak</Label>
            <Input type="number" min="0" step="1"
              value={(emp.marital_status === "TIDAK_KAWIN" || emp.marital_status === "BELUM_KAWIN") ? 0 : (emp.number_of_children ?? 0)}
              onChange={(e) => {
                const n = Math.max(0, parseInt(e.target.value || "0", 10));
                setEmp({ ...emp, number_of_children: isNaN(n) ? 0 : n });
              }}
              disabled={!canEdit || emp.marital_status === "TIDAK_KAWIN" || emp.marital_status === "BELUM_KAWIN" || !emp.marital_status}
              data-testid="emp-children" />
          </div>
          <div><Label>Tingkat Pendidikan</Label>
            <Select value={emp.education_level || ""} onValueChange={(v) => setEmp({ ...emp, education_level: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-education"><SelectValue placeholder="Pilih tingkat" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SD">SD</SelectItem>
                <SelectItem value="SMP">SMP</SelectItem>
                <SelectItem value="SMA">SMA</SelectItem>
                <SelectItem value="Diploma">Diploma</SelectItem>
                <SelectItem value="S1">S1</SelectItem>
                <SelectItem value="S2">S2</SelectItem>
                <SelectItem value="S3">S3</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Email Pribadi</Label>
            <Input type="email" placeholder="nama@email.com"
              value={emp.personal_email || ""}
              onChange={(e) => setEmp({ ...emp, personal_email: e.target.value })}
              disabled={!canEdit} data-testid="emp-personal-email" />
          </div>
          <div><Label>HP</Label><Input value={emp.phone || ""} onChange={(e) => setEmp({ ...emp, phone: e.target.value })} disabled={!canEdit} data-testid="emp-phone" /></div>
        </div>
        <div>
          <Label>Alamat Sesuai KTP</Label>
          <textarea
            value={emp.ktp_address || ""}
            onChange={(e) => setEmp({ ...emp, ktp_address: e.target.value })}
            disabled={!canEdit}
            placeholder="Jalan, No., RT/RW, Kel./Desa, Kec., Kota/Kab., Provinsi, Kode Pos"
            maxLength={1000}
            rows={3}
            className="flex min-h-[80px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="emp-ktp-address"
          />
          <p className="text-xs text-slate-500 mt-1">{(emp.ktp_address || "").length} / 1000 karakter</p>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Kontrak & Gaji</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <Label>Jabatan*</Label>
            <Select value={emp.position_code || ""} onValueChange={handlePositionChange} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-position"><SelectValue placeholder="Pilih jabatan" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {positions.map((p) => (
                  <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Departemen*</Label>
            <Select value={emp.department_code || ""} onValueChange={(v) => setEmp({ ...emp, department_code: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-department"><SelectValue placeholder="Pilih departemen" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {departments.map((d) => (
                  <SelectItem key={d.code} value={d.code}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Jenis Kontrak</Label>
            <Select value={emp.contract_type} onValueChange={(v) => setEmp({ ...emp, contract_type: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-contract-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PKWTT">PKWTT (Permanen)</SelectItem>
                <SelectItem value="PKWT">PKWT (Kontrak)</SelectItem>
                <SelectItem value="INTERNSHIP">Internship</SelectItem>
                <SelectItem value="FREELANCE">Freelance</SelectItem>
                <SelectItem value="OUTSOURCE">Outsource</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Tgl Masuk*</Label><Input type="date" value={emp.join_date || ""} onChange={(e) => setEmp({ ...emp, join_date: e.target.value })} disabled={!canEdit} data-testid="emp-join" /></div>
          <div><Label>Tgl Kontrak Berakhir</Label><Input type="date" value={emp.contract_end_date || ""} onChange={(e) => setEmp({ ...emp, contract_end_date: e.target.value })} disabled={!canEdit} data-testid="emp-end" /></div>
          <div>
            <Label>Email Kantor</Label>
            <Input
              type="email"
              value={emp.work_email || ""}
              onChange={(e) => { setEmp({ ...emp, work_email: e.target.value }); validateEmail(e.target.value); }}
              onBlur={(e) => validateEmail(e.target.value)}
              placeholder="nama@abs.co.id (opsional)"
              disabled={!canEdit && !isNew}
              className={emailError ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="emp-work-email"
            />
            {emailError && (
              <div className="text-xs text-red-600 mt-1" data-testid="emp-work-email-error">
                {emailError}
              </div>
            )}
          </div>
          <div><Label>Gaji Pokok (IDR)</Label><Input type="number" value={emp.current_basic_salary || 0} onChange={(e) => setEmp({ ...emp, current_basic_salary: Number(e.target.value) })} disabled={!canEdit} data-testid="emp-salary" /></div>
          <div><Label>Status</Label>
            <Select value={emp.status} onValueChange={(v) => setEmp({ ...emp, status: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="emp-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Aktif</SelectItem>
                <SelectItem value="PROBATION">Probation</SelectItem>
                <SelectItem value="INACTIVE">Nonaktif</SelectItem>
                <SelectItem value="RESIGNED">Resign</SelectItem>
                <SelectItem value="TERMINATED">Diterminasi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-slate-900">Bank & BPJS</h3>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Nama Bank</Label><Input value={emp.bank_code || ""} onChange={(e) => setEmp({ ...emp, bank_code: e.target.value })} disabled={!canEdit} data-testid="emp-bank" /></div>
          <div><Label>Nomor Rekening</Label><Input value={emp.bank_account_number || ""} onChange={(e) => setEmp({ ...emp, bank_account_number: e.target.value })} disabled={!canEdit} data-testid="emp-rek" /></div>
          <div><Label>Nama Pemilik</Label><Input value={emp.bank_account_holder || ""} onChange={(e) => setEmp({ ...emp, bank_account_holder: e.target.value })} disabled={!canEdit} data-testid="emp-holder" /></div>
          <div><Label>BPJS Kesehatan</Label><Input value={emp.bpjs_kesehatan_number || ""} onChange={(e) => setEmp({ ...emp, bpjs_kesehatan_number: e.target.value })} disabled={!canEdit} data-testid="emp-bpjs-kes" /></div>
          <div><Label>BPJS Ketenagakerjaan</Label><Input value={emp.bpjs_ketenagakerjaan_number || ""} onChange={(e) => setEmp({ ...emp, bpjs_ketenagakerjaan_number: e.target.value })} disabled={!canEdit} data-testid="emp-bpjs-tk" /></div>
        </div>
      </CardContent></Card>
      {!isNew && history.length > 0 && (
        <Card><CardContent className="p-4">
          <h3 className="font-semibold text-slate-900 mb-2">Riwayat Perubahan</h3>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Tanggal</TableHead><TableHead>Event</TableHead>
              <TableHead>Perubahan</TableHead><TableHead>Alasan</TableHead>
              <TableHead>Oleh</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{h.recorded_at?.slice(0, 10)}</TableCell>
                  <TableCell><Badge variant="outline">{h.event_type}</Badge></TableCell>
                  <TableCell className="text-xs">{JSON.stringify(h.after)}</TableCell>
                  <TableCell className="text-xs">{h.reason || "-"}</TableCell>
                  <TableCell>{h.recorded_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
    </div>
  );
}

export function HRPolicyListPage() {
  const can = useCanHR();
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    api.get("/hr-policy/types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);
  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (type) q.set("type", type);
      const r = await api.get(`/hr-policy?${q.toString()}`);
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (can("HR_POLICY_LIHAT")) load(); /* eslint-disable-next-line */ }, [type]);
  if (!can("HR_POLICY_LIHAT")) return <NoAccessBanner perm="HR_POLICY_LIHAT" />;
  return (
    <div className="space-y-4" data-testid="hr-policy-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">HR Policy Engine</h1>
          <p className="text-sm text-slate-600">Working hours, BPJS, cuti, THR, overtime — versioned + effective-dated. Historical payroll pakai snapshot.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-64" data-testid="policy-type-filter"><SelectValue placeholder="Semua Type" /></SelectTrigger>
            <SelectContent>
              {types.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        ⚠️ Nilai default seed berdasarkan regulasi 2026 (informasi umum). <b>Wajib verifikasi ke regulasi terkini</b> (SKB 3 Menteri, PMK terbaru, aturan BPJS) sebelum payroll production.
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Kode</TableHead><TableHead>Nama</TableHead>
            <TableHead>Type</TableHead><TableHead>Versi</TableHead>
            <TableHead>Effective From</TableHead>
            <TableHead>Effective Until</TableHead>
            <TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7}>Memuat…</TableCell></TableRow>
            : rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-500">Belum ada policy.</TableCell></TableRow>
            : rows.map((r) => (
              <TableRow key={r.id} data-testid={`policy-row-${r.id}`}>
                <TableCell className="font-mono">{r.code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell><Badge variant="outline">{r.type}</Badge></TableCell>
                <TableCell>v{r.version}</TableCell>
                <TableCell>{r.effective_from}</TableCell>
                <TableCell>{r.effective_until || "—"}</TableCell>
                <TableCell><Badge className={r.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100"}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

export default { HRHub, EmployeeListPage, EmployeeEditPage, HRPolicyListPage };
